var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Card, Typography, Button, Select, MenuItem, FormControl, InputLabel, } from "@mui/material";
import BloodDrop from "../BloodDrop/BloodDrop";
import { useEffect, useState } from "react";
import { getStock, getServiceUnits } from "../../apis/api";
const TIPOS_SANGUE = [
    { tipo: "a_negativo", label: "A-" },
    { tipo: "a_positivo", label: "A+" },
    { tipo: "ab_negativo", label: "AB-" },
    { tipo: "b_negativo", label: "B-" },
    { tipo: "b_positivo", label: "B+" },
    { tipo: "ab_positivo", label: "AB+" },
    { tipo: "o_negativo", label: "O-" },
    { tipo: "o_positivo", label: "O+" },
];
const STATUS_LEVELS = {
    CRITICO: 30,
    ALERTA: 60,
};
const STATUS_COLORS = {
    CRITICO: "#BE0007",
    ALERTA: "#F2A900",
    ESTAVEL: "#1CB58D",
};
function getStatus(level) {
    if (level < STATUS_LEVELS.CRITICO)
        return { label: "Crítico", color: STATUS_COLORS.CRITICO };
    if (level < STATUS_LEVELS.ALERTA)
        return { label: "Alerta", color: STATUS_COLORS.ALERTA };
    return { label: "Estável", color: STATUS_COLORS.ESTAVEL };
}
const getFormattedDate = (data) => {
    if (!data)
        return new Date().toLocaleDateString("pt-BR");
    const dataObj = new Date(data);
    return !isNaN(dataObj.getTime())
        ? dataObj.toLocaleDateString("pt-BR")
        : new Date().toLocaleDateString("pt-BR");
};
export const BloodStock = ({ estoque: initialStock, data_atualizacao: initialUpdateDate, }) => {
    const [stock, setStock] = useState(initialStock);
    const [updateDate, setUpdateDate] = useState(initialUpdateDate);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState("");
    const [allStocks, setAllStocks] = useState([]);
    const [serviceUnits, setServiceUnits] = useState([]);
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsLoading(true);
            setError(null);
            try {
                const [estoquesResponse, unidadesResponse] = yield Promise.all([
                    getStock(),
                    getServiceUnits(),
                ]);
                setAllStocks(estoquesResponse);
                setServiceUnits(unidadesResponse);
                if (unidadesResponse.length > 0) {
                    const hemocentroCoordenador = unidadesResponse.find((unidade) => unidade.nome === "Hemocentro Coordenador");
                    const unidadeInicial = (hemocentroCoordenador === null || hemocentroCoordenador === void 0 ? void 0 : hemocentroCoordenador.id) || unidadesResponse[0].id;
                    setSelectedUnit(unidadeInicial);
                }
            }
            catch (error) {
                console.error("Erro ao buscar dados:", error);
                setError("Não foi possível carregar os dados");
            }
            finally {
                setIsLoading(false);
            }
        });
        fetchData();
    }, []);
    useEffect(() => {
        if (allStocks.length === 0 || !selectedUnit)
            return;
        const estoqueSelecionado = allStocks.find((estoque) => { var _a; return ((_a = estoque.Unidade) === null || _a === void 0 ? void 0 : _a.id) === selectedUnit; });
        if (estoqueSelecionado) {
            const getLevel = (qtd) => qtd;
            const estoques = TIPOS_SANGUE.map(({ tipo, label }) => ({
                tipo,
                label,
                level: getLevel(estoqueSelecionado[tipo]),
                quantidade: estoqueSelecionado[tipo],
            }));
            setStock(estoques);
            setUpdateDate(getFormattedDate(estoqueSelecionado.date_updated) ||
                getFormattedDate(estoqueSelecionado.date_created) ||
                new Date().toLocaleDateString("pt-BR"));
        }
        else {
            const estoqueCoordenador = allStocks.find((e) => { var _a; return ((_a = e.Unidade) === null || _a === void 0 ? void 0 : _a.nome) === "Hemocentro Coordenador"; });
            const estoqueParaUsar = estoqueCoordenador || allStocks.find((e) => e.Unidade !== null);
            if (estoqueParaUsar) {
                const getLevel = (qtd) => qtd;
                const estoques = TIPOS_SANGUE.map(({ tipo, label }) => ({
                    tipo,
                    label,
                    level: getLevel(estoqueParaUsar[tipo]),
                    quantidade: estoqueParaUsar[tipo],
                }));
                setStock(estoques);
                setUpdateDate(getFormattedDate(estoqueParaUsar.date_updated) ||
                    getFormattedDate(estoqueParaUsar.date_created) ||
                    new Date().toLocaleDateString("pt-BR"));
                if (estoqueParaUsar.Unidade) {
                    setSelectedUnit(estoqueParaUsar.Unidade.id);
                }
            }
        }
    }, [selectedUnit, allStocks]);
    if (error) {
        return (_jsx(Card, Object.assign({ sx: { p: 2, textAlign: "center", color: STATUS_COLORS.CRITICO } }, { children: _jsx(Typography, { children: error }) })));
    }
    return (_jsxs(Card, Object.assign({ sx: {
            borderRadius: 4,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            background: "var(--hemoes-bloodstock-card-bg)",
            p: 0,
            overflow: "visible",
            maxWidth: 1100,
        } }, { children: [_jsxs(Box, Object.assign({ sx: {
                    background: "var(--hemoes-bloodstock-header-bg)",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    borderBottom: "var(--hemoes-bloodstock-header-border-width) solid var(--hemoes-bloodstock-header-border-color)",
                    px: 4,
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                } }, { children: [_jsxs(Typography, Object.assign({ variant: "h6", sx: {
                            color: "var(--hemoes-bloodstock-header-text)",
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 700,
                            fontSize: {
                                xs: "calc(16px + var(--accessibility-font-delta, 0px))",
                                sm: "calc(22px + var(--accessibility-font-delta, 0px))",
                            },
                            lineHeight: { xs: "20px", sm: "30px" },
                            letterSpacing: "1px",
                            verticalAlign: "middle",
                            textAlign: { xs: "center", sm: "left" },
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "center", sm: "flex-start" },
                            gap: { xs: 0.5, sm: 0 },
                        } }, { children: [_jsxs("span", { children: ["Estoque atualizado em ", updateDate] }), _jsxs(Typography, Object.assign({ component: "span", sx: {
                                    color: "var(--hemoes-bloodstock-header-text)",
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 800,
                                    fontSize: {
                                        xs: "calc(14px + var(--accessibility-font-delta, 0px))",
                                        sm: "calc(16px + var(--accessibility-font-delta, 0px))",
                                    },
                                    lineHeight: { xs: "16px", sm: "24px" },
                                    letterSpacing: "0.5px",
                                    verticalAlign: "middle",
                                    textDecoration: "underline",
                                    whiteSpace: "nowrap",
                                } }, { children: ["(Fonte:", " ", _jsx("a", Object.assign({ href: "https://dados.es.gov.br", style: {
                                            color: "var(--hemoes-bloodstock-header-text)",
                                            textDecoration: "underline",
                                        }, target: "_blank", rel: "noopener noreferrer" }, { children: "Dados Abertos ES" })), ")"] }))] })), _jsxs(FormControl, Object.assign({ sx: {
                            minWidth: { xs: "100%", sm: "200px" },
                            maxWidth: { xs: "100%", sm: "250px" },
                        }, size: "small" }, { children: [_jsx(InputLabel, Object.assign({ sx: {
                                    color: "var(--hemoes-bloodstock-header-text)",
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                    fontSize: "calc(14px + var(--accessibility-font-delta, 0px))",
                                    "&.Mui-focused": {
                                        color: "var(--hemoes-bloodstock-header-text)",
                                    },
                                } }, { children: "Hemocentro" })), _jsx(Select, Object.assign({ value: selectedUnit || "", onChange: (e) => setSelectedUnit(e.target.value), label: "Hemocentro", "data-testid": "blood-stock-unit-select", sx: {
                                    color: "var(--hemoes-bloodstock-select-text)",
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                    fontSize: "calc(14px + var(--accessibility-font-delta, 0px))",
                                    backgroundColor: "var(--hemoes-bloodstock-select-bg)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "var(--hemoes-bloodstock-select-border)",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "var(--hemoes-bloodstock-select-border-hover)",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "var(--hemoes-bloodstock-select-border-focus)",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "var(--hemoes-bloodstock-select-text)",
                                    },
                                }, MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                            fontFamily: "Montserrat, sans-serif",
                                            backgroundColor: "var(--hemoes-bloodstock-select-bg)",
                                            color: "var(--hemoes-bloodstock-select-text)",
                                        },
                                    },
                                } }, { children: serviceUnits.map((unidade) => (_jsx(MenuItem, Object.assign({ value: unidade.id, sx: {
                                        fontFamily: "Montserrat, sans-serif",
                                        fontWeight: 500,
                                        fontSize: "calc(14px + var(--accessibility-font-delta, 0px))",
                                    } }, { children: unidade.nome }), unidade.id))) }))] }))] })), _jsx(Box, Object.assign({ sx: {
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "center",
                    alignItems: { xs: "stretch", md: "flex-end" },
                    gap: { xs: 3, sm: 2, md: 2 },
                    px: { xs: 4, md: 6 },
                    py: { xs: 6, md: 4 },
                    maxWidth: { xs: "100%", md: "90%" },
                    width: "100%",
                    margin: "0 auto",
                    flexWrap: "nowrap",
                    background: "var(--hemoes-bloodstock-body-bg)",
                } }, { children: isLoading ? (_jsx(Typography, Object.assign({ sx: {
                        textAlign: "center",
                        width: "100%",
                        color: "var(--hemoes-bloodstock-body-text)",
                        fontSize: "calc(14px + var(--accessibility-font-delta, 0px))",
                    } }, { children: "Carregando..." }))) : (stock === null || stock === void 0 ? void 0 : stock.map((sangue) => {
                    const status = getStatus(sangue.level);
                    return (_jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            flexDirection: { xs: "row", md: "column" },
                            alignItems: { xs: "center", md: "center" },
                            justifyContent: { xs: "space-between", md: "center" },
                            mx: { xs: 0, sm: 2 },
                            width: { xs: "100%", md: "auto" },
                            px: { xs: 2, sm: 0 },
                        } }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle1", sx: {
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 800,
                                    fontSize: {
                                        xs: "calc(20px + var(--accessibility-font-delta, 0px))",
                                        sm: "calc(16px + var(--accessibility-font-delta, 0px))",
                                    },
                                    lineHeight: { xs: "24px", sm: "24px" },
                                    letterSpacing: "0.5px",
                                    color: "var(--hemoes-bloodstock-body-text)",
                                    mb: { xs: 0, md: 1.5 },
                                    width: { xs: "80px", md: "auto" },
                                    textAlign: { xs: "center", md: "center" },
                                } }, { children: sangue.label })), _jsx(Box, Object.assign({ sx: {
                                    width: { xs: 90, sm: 82 },
                                    height: { xs: 90, sm: 82 },
                                    gap: "10px",
                                    padding: "5px",
                                    borderRadius: "100px",
                                    background: "#D6D7E0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: { xs: 0, md: 1.5 },
                                    boxShadow: "0px 1px 3.8px -4px #8FC9F980",
                                } }, { children: _jsx(BloodDrop, { level: sangue.level, type: sangue.label, color: "var(--hemoes-bloodstock-drop)" }) })), _jsx(Typography, Object.assign({ variant: "subtitle2", className: "hemoes-bloodstock-status", sx: {
                                    color: status.color,
                                    fontWeight: 700,
                                    fontSize: {
                                        xs: "calc(18px + var(--accessibility-font-delta, 0px))",
                                        sm: "calc(14px + var(--accessibility-font-delta, 0px))",
                                    },
                                    lineHeight: { xs: "22px", sm: "20px" },
                                    mt: { xs: 0, md: 0.5 },
                                    width: { xs: "80px", md: "auto" },
                                    textAlign: { xs: "center", md: "center" },
                                } }, { children: status.label }))] }), sangue.label));
                })) })), _jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "center", pb: 4, mt: 1 } }, { children: _jsx(Button, Object.assign({ variant: "contained", sx: {
                        background: "var(--hemoes-bloodstock-button-bg)",
                        color: "var(--hemoes-bloodstock-button-text)",
                        height: "42px",
                        width: { xs: "90%", sm: "421px" },
                        borderRadius: "35px",
                        padding: "16px 24px",
                        fontWeight: 600,
                        fontSize: "calc(16px + var(--accessibility-font-delta, 0px))",
                        boxShadow: "0 5px 15px 0 rgba(0,0,0,0.25)",
                        textTransform: "none",
                        "&:hover": {
                            background: "var(--hemoes-bloodstock-button-hover-bg)",
                            color: "var(--hemoes-bloodstock-button-hover-text)",
                            boxShadow: "0 5px 15px 0 rgba(0,0,0,0.35)",
                        },
                    }, href: "/app/agendamento/one/doacao-de-sangue" }, { children: "Quero Doar" })) }))] })));
};
